import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CtaBanner } from "./Home";
import { PROJECTS, slugify } from "../data/site";

const FILTERS = ["All", "Residential", "Commercial", "Industrial", "Educational"];

const Projects = () => {
  const [filter, setFilter] = useState("All");
  const list = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div data-testid="projects-page">
      <PageHero eyebrow="OUR PROJECTS" title="Proven mechanical solutions, delivered with confidence"
        subtitle="From residential developments and private residences to large commercial, industrial and educational facilities, we deliver mechanical solutions engineered for performance, efficiency and long-term reliability."
        image={IMG_HERO} crumbs={[{ label: "Projects" }]} />

      <section className="pt-20 md:pt-28 bg-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <p className="text-js-charcoal/60 text-lg leading-relaxed">
              Our project portfolio demonstrates experience across air conditioning, VRV and split systems, mechanical ventilation, centralised controls and car park exhaust systems. We work closely with builders, developers and project teams to deliver quality mechanical installations from planning through to completion.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap gap-2.5 mb-12">
            {FILTERS.map((f) => (
              <button key={f} data-testid={`filter-${f.toLowerCase()}`} onClick={() => setFilter(f)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${filter === f ? "bg-js-blue text-white" : "bg-js-grey text-js-charcoal/70 hover:bg-js-blue/10 hover:text-js-blue"}`}>
                {f}
              </button>
            ))}
          </div>
          <motion.div layout className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {list.map((p) => (
                <motion.div key={p.title} layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.4 }}>
                  <Link to={`/projects/${slugify(p.title)}`} data-testid="project-item"
                    className="group block bg-js-grey rounded-3xl overflow-hidden border border-black/5 hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)] transition-shadow duration-400 h-full">
                    <div className="relative h-56 overflow-hidden">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-js-blue text-xs font-semibold">{p.category}</span>
                      {p.status && (
                        <span className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold ${p.status === "In Progress" ? "bg-js-red text-white" : "bg-js-charcoal/80 text-white backdrop-blur"}`}>{p.status}</span>
                      )}
                    </div>
                    <div className="p-7">
                      {p.client && <p className="text-js-charcoal/45 text-xs font-semibold tracking-wide">{p.client.toUpperCase()}</p>}
                      <h3 className="font-display font-bold text-lg mt-1">{p.title}</h3>
                      <p className="text-js-charcoal/50 text-sm flex items-center gap-1.5 mt-1"><MapPin size={13} className="text-js-blue" /> {p.location} · {p.system}</p>
                      <p className="text-js-charcoal/60 text-sm mt-3 line-clamp-2">{p.description || p.solution}</p>
                      <span className="flex items-center gap-2 text-js-blue font-semibold text-sm mt-4 group-hover:gap-3 transition-all duration-300">View Project <ArrowRight size={16} /></span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-js-grey">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <span className="text-js-blue font-semibold text-sm tracking-wide">OUR APPROACH</span>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-3 text-balance">Built on experience. Delivered with precision.</h2>
            <p className="text-js-charcoal/60 leading-relaxed mt-6">Every project presents different requirements. Our approach is to understand the building, coordinate closely with the project team and deliver mechanical systems that perform reliably long after installation.</p>
            <p className="font-display font-bold text-js-charcoal text-lg mt-8">Residential. Commercial. Industrial. Educational.</p>
            <p className="text-js-charcoal/60 leading-relaxed mt-3">From individual residences to multi-unit developments and large-scale facilities, we bring practical experience, quality equipment and professional workmanship to every project.</p>
          </Reveal>
        </div>
      </section>
      <CtaBanner />
    </div>
  );
};

const IMG_HERO = PROJECTS[0].image;

export default Projects;
